import { Component } from '@angular/core';
import {
  COMMON_IMPORTS,
  MATERIAL_IMPORTS,
} from '../../shared/imports/common-imports';
import { GraphViewComponent } from '../general-view/graph-view/graph-view.component';
import { RiskScoreDiagramComponent } from '../../shared/components/risk-score-diagram/risk-score-diagram.component';
import { ListViewComponent } from '../general-view/list-view/list-view.component';
import {
  ComplianceData,
  RiskData,
  RiskFlagData,
} from '../../shared/models/compliance-data.model';
import { LambdaApiService } from '../../shared/services/lambda-api.service';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { DialogService } from '../../shared/services/dialog.service';
import { THERAPEUTIC_AREAS } from '../../shared/data/therapeutic-areas';
import ComplianceElements from '../../shared/data/compliance-elements';
import { RiskScoreService } from '../../shared/services/risk-score.service';
import { FlagType } from '../../shared/models/top-vendors-data.model';
import { EmptyVendorFlagsPercentages } from '../../shared/models/percentages-response.model';
import { FilterVisualizerComponent } from '../../shared/components/filter-visualizer/filter-visualizer.component';
import { CalendarService } from '../../shared/services/calendar.service';

@Component({
  selector: 'app-global-compliance',
  standalone: true,
  imports: [
    MATERIAL_IMPORTS,
    COMMON_IMPORTS,
    GraphViewComponent,
    RiskScoreDiagramComponent,
    ListViewComponent,
    SpinnerComponent,
    FilterVisualizerComponent,
  ],
  templateUrl: './global-compliance.component.html',
  styleUrl: './global-compliance.component.scss',
})
export class GlobalComplianceComponent {
  currentView: 'list' | 'graph' = 'graph';
  data: ComplianceData[] = [];
  isLoading = false;
  percentages = [
    {
      flagType: FlagType.OPERATIONAL,
      percentage: EmptyVendorFlagsPercentages,
    },
    {
      flagType: FlagType.FINANCIAL,
      percentage: EmptyVendorFlagsPercentages,
    },
  ];
  percentageSum = { ...EmptyVendorFlagsPercentages };
  firstDate = '';
  lastDate = '';

  constructor(
    private readonly apiService: LambdaApiService,
    private readonly dialogService: DialogService,
    private readonly riskScoreService: RiskScoreService,
    private readonly calendarService: CalendarService,
  ) {}

  ngOnInit() {
    this.isLoading = true;
    this.loadData();
  }

  loadData() {
    this.isLoading = true;

    const percentages = this.percentages.map((p) => {
      return this.apiService.getPercentages(p.flagType).then((data) => {
        p.percentage = {
          high: data.high,
          medium: data.medium,
          low: data.low,
        };
      });
    });

    const availableMonthsPromise = this.apiService
      .getAvailableMonths()
      .then((months) => {
        const years = Object.keys(months.months).sort();
        const firstYear = years[0];
        const firstMonth = months.months[firstYear][0];

        const lastYear = years[years.length - 1];
        const lastMonth =
          months.months[lastYear][months.months[lastYear].length - 1];

        this.calendarService.firstDate = {
          year: Number.parseInt(firstYear, 10),
          month: Number.parseInt(firstMonth, 10),
        };
        this.calendarService.lastDate = {
          year: Number.parseInt(lastYear, 10),
          month: Number.parseInt(lastMonth, 10),
        };

        this.firstDate = this.calendarService.getFormatedDateLimit(true);
        this.lastDate = this.calendarService.getFormatedDateLimit(false);
      });

    const promise = this.apiService
      .fetchComplianceData()
      .then((data: RiskData) => {
        const formattedData = this.formatData(data);
        this.data = formattedData;
      });

    Promise.all([...percentages, promise, availableMonthsPromise]).then(() => {
      this.sumPercentages();
      this.isLoading = false;
    });
  }

  sumPercentages() {
    this.percentageSum = this.riskScoreService.sumPercentages(
      this.percentages.map((p) => p.percentage),
    );
    //temporarly set the percentages to the "Assessing Risk" element
    //MVP only has data for this element
    const assesingRiskElement = this.data[1].elements.find(
      (e) => e.elementName === 'Assessing Risk',
    );

    if (assesingRiskElement) {
      assesingRiskElement.percentages = this.percentageSum;

      //re calculate colors
      const colors = this.riskScoreService.calculatePercentageColor(
        this.percentageSum,
      );
      assesingRiskElement.color = colors.color;
      assesingRiskElement.colorName = colors.colorName;
    }
  }

  formatData(data: RiskData) {
    const flags: RiskFlagData = {
      f1: 0,
      f2: 0,
      f3: 0,
      f4: 0,
      f5: 0,
      f6: 0,
    };

    data.regions.forEach((region) => {
      flags.f1 += region.totals.f1;
      flags.f2 += region.totals.f2;
      flags.f3 += region.totals.f3;
      flags.f4 += region.totals.f4;
      flags.f5 += region.totals.f5;
      flags.f6 += region.totals.f6;
    });

    const tas = THERAPEUTIC_AREAS;
    const complianceElements = ComplianceElements;
    const outputData: ComplianceData[] = [
      {
        id: tas[1].id,
        therapeuticArea: tas[1],
        //MVP will have 2 TAs without data, therefore 0 is hardcoded
        elements: complianceElements.map((element) => {
          const value = 0;
          const colors = this.riskScoreService.calculatePercentageColor(
            EmptyVendorFlagsPercentages,
          );
          return {
            elementName: element.name,
            value,
            link: element.link,
            icon: element.icon,
            color: colors.color,
            colorName: colors.colorName,
            percentages: EmptyVendorFlagsPercentages,
          };
        }),
      },
      {
        id: tas[0].id,
        therapeuticArea: tas[0],
        elements: complianceElements.map((element) => {
          const flagSum = Object.values(flags).reduce(
            (sum, val) => sum + val,
            0,
          );
          const value =
            element.name.toLocaleLowerCase() === 'assessing risk' ? flagSum : 0;
          let colors = this.riskScoreService.calculatePercentageColor(
            EmptyVendorFlagsPercentages,
          );
          return {
            elementName: element.name,
            value,
            link: element.link,
            icon: element.icon,
            color: colors.color,
            colorName: colors.colorName,
            percentages: EmptyVendorFlagsPercentages,
          };
        }),
      },
      {
        id: tas[2].id,
        therapeuticArea: tas[2],
        //MVP will have 2 TAs without data, therefore 0 is hardcoded
        elements: complianceElements.map((element) => {
          const value = 0;
          const colors = this.riskScoreService.calculatePercentageColor(
            EmptyVendorFlagsPercentages,
          );
          return {
            elementName: element.name,
            value,
            link: element.link,
            icon: element.icon,
            color: colors.color,
            colorName: colors.colorName,
            percentages: EmptyVendorFlagsPercentages,
          };
        }),
      },
    ];

    return outputData;
  }

  setView(view: 'list' | 'graph') {
    this.currentView = view;
  }

  openDateRangePicker() {
    const dialogRef = this.dialogService.openDateRangePicker();

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.refresh) {
        this.reloadData();
      }
    });
  }

  openFilterDialog() {
    const dialogRef = this.dialogService.openFilterDialog();

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.refresh) {
        this.reloadData();
      }
    });
  }

  reloadData() {
    this.loadData();
  }
}
